import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { useCallback } from "react";
import { useActiveWorkspaceScopeKey } from "@/lib/active-workspace";
import { swrFetcher } from "@/lib/swr-fetcher";
import { fetcher } from "@/lib/fetcher";
import {
  getOnboardingProgressCacheKey,
  isOnboardingProgressCacheKey,
} from "@/services/onboarding-service";
import {
  OnboardingProgressSchema,
  OnboardingProgress,
  CollapseStateUpdateSchema,
  ApiResponseSchema,
} from "../schemas/onboarding-schemas";
import type { ApiResponse } from "../model";

// Fetch onboarding progress for the current user
export function useOnboardingProgress(enabled: boolean = true) {
  const workspaceScope = useActiveWorkspaceScopeKey();
  const progressKey =
    enabled && workspaceScope !== "pending"
      ? getOnboardingProgressCacheKey(workspaceScope)
      : null;
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    ApiResponse<OnboardingProgress[]>
  >(progressKey, ([url]) =>
    swrFetcher(url, ApiResponseSchema(OnboardingProgressSchema)),
  );

  return {
    onboardingProgress: data?.data?.[0] || null,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Hook for updating collapse state
export function useUpdateCollapseState() {
  const { mutate } = useSWRConfig();

  const { trigger } = useSWRMutation(
    "/api/ai-transform/v1/onboarding/collapse",
    async (url: string, { arg }: { arg: boolean }) => {
      const validatedPayload = CollapseStateUpdateSchema.parse({
        is_collapsed: arg,
      });
      const raw = await fetcher<unknown>(url, {
        method: "PUT",
        body: JSON.stringify(validatedPayload),
      });
      const validatedResult = ApiResponseSchema(OnboardingProgressSchema).parse(
        raw,
      );

      if (validatedResult.status === "OK" && validatedResult.data?.[0]) {
        mutate((key) => isOnboardingProgressCacheKey(key));
        return validatedResult.data[0];
      }

      throw new Error(
        validatedResult.message || "Failed to update collapse state",
      );
    },
  );

  const updateCollapseState = useCallback(
    async (isCollapsed: boolean): Promise<OnboardingProgress | null> => {
      return trigger(isCollapsed);
    },
    [trigger],
  );

  return updateCollapseState;
}
